import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import JsonLd from '@/components/base/JsonLd';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { aboutInfo } from '@/mocks/siteData';

export default function AboutPage() {
  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 猫咪视频大全',
    description: aboutInfo.brandIntro,
    url: 'https://catvideo.cn/about',
    about: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      description: aboutInfo.mission,
      email: aboutInfo.contact.email,
      address: {
        '@type': 'PostalAddress',
        streetAddress: aboutInfo.contact.address,
        addressLocality: '杭州',
        addressRegion: '浙江',
        addressCountry: 'CN',
      },
    },
  };

  return (
    <>
      <SEOHead
        title="关于我们｜品牌介绍与联系方式"
        description={`${aboutInfo.brandIntro.slice(0, 120)}`}
        keywords="猫咪视频大全,关于我们,品牌介绍,猫咪视频平台,联系方式"
        canonicalUrl="https://catvideo.cn/about"
        ogImage="https://readdy.ai/api/search-image?query=Warm%20cozy%20office%20with%20cats%20walking%20around%20friendly%20team%20working%20happily%20cream%20and%20warm%20peach%20tones%20professional%20yet%20inviting%20atmosphere%20editorial%20photography&width=1200&height=630&seq=og-about&orientation=landscape"
        ogImageWidth={1200}
        ogImageHeight={630}
      />
      <JsonLd data={aboutSchema} />

      <div className="min-h-screen">
        <Navbar />
        <main className="pt-16">
          <section className="bg-accent-50">
            <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: '关于我们' },
                ]}
              />
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
                关于我们
              </h1>
              <p className="text-foreground-500 text-sm md:text-base max-w-2xl">
                了解猫咪视频大全的品牌故事与团队理念
              </p>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div className="text-center mb-14">
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-4">
                  品牌故事
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-5">
                  每一个爱猫人的温暖港湾
                </h2>
                <p className="text-foreground-500 leading-relaxed text-sm md:text-base max-w-2xl mx-auto">
                  {aboutInfo.brandIntro}
                </p>
              </div>

              <div className="text-center mb-14">
                <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-800 mb-4">
                  我们的使命
                </h3>
                <p className="text-foreground-500 leading-relaxed text-sm md:text-base max-w-2xl mx-auto">
                  {aboutInfo.mission}
                </p>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
                {aboutInfo.stats.map((stat, i) => (
                  <div key={i} className="text-center p-5 md:p-6 rounded-2xl bg-background-100">
                    <div className="font-heading text-2xl md:text-3xl font-bold text-primary-500 mb-1">
                      {stat.number}
                    </div>
                    <div className="text-xs md:text-sm text-foreground-500">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-sm font-medium mb-4">
                  核心价值观
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800">
                  我们坚持的准则
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {aboutInfo.values.map((val, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-background-50 border border-background-200/70 text-center">
                    <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-secondary-100 flex items-center justify-center text-secondary-600">
                      <i className={`text-xl ${
                        ['ri-star-line', 'ri-refresh-line', 'ri-user-heart-line', 'ri-heart-3-line'][i]
                      }`}></i>
                    </div>
                    <h3 className="font-heading text-base font-bold text-foreground-800 mb-2">{val.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{val.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-50">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-700 text-sm font-medium mb-4">
                  用户口碑
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800">
                  用户怎么说
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
                {aboutInfo.testimonials.map((t, i) => (
                  <div key={i} className="p-6 rounded-2xl bg-background-100 border border-background-200/70">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center text-accent-700 font-heading font-bold text-sm">
                        {t.avatar}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-800">{t.name}</div>
                        <div className="text-xs text-foreground-400">{t.role}</div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-500 leading-relaxed">{t.content}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-[700px] mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <span className="inline-block px-4 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-sm font-medium mb-4">
                  联系我们
                </span>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-800 mb-3">
                  期待你的声音
                </h2>
                <p className="text-foreground-500 text-sm md:text-base">
                  有任何建议或合作意向，欢迎随时联系我们
                </p>
              </div>

              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-primary-100 flex items-center justify-center text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">邮箱</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-accent-100 flex items-center justify-center text-accent-600">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">微信</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex-shrink-0 rounded-xl bg-secondary-100 flex items-center justify-center text-secondary-600">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-800 mb-1">地址</h4>
                      <p className="text-sm text-foreground-500">{aboutInfo.contact.address}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}